import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PresenceDebouncer, ConnectionMonitor, type PresenceUpdate } from '@/utils/presenceUtils';
import { presenceRequestManager } from '@/utils/presenceRequestManager';
import { timeoutManager } from '@/utils/timeoutManager';

export type CardPresenceUser = {
  id: string;
  board_id: string;
  card_id: string;
  user_id: string;
  user_name: string | null;
  status: "viewing" | "editing";
  updated_at: string;
};

type CardPresenceState = {
  [cardId: string]: CardPresenceUser[];
};

interface CardPresenceContextType {
  presence: CardPresenceState;
  isConnected: boolean;
  connectionHealth: boolean;
  setCardStatus: (cardId: string, status: "viewing" | "editing") => void;
  clearCardPresence: (cardId: string) => void;
  trackCardVisibility: (cardId: string, isVisible: boolean) => void;
}

const CardPresenceContext = createContext<CardPresenceContextType | undefined>(undefined);

interface CardPresenceProviderProps {
  children: React.ReactNode;
  boardId: string;
}

export function useOptimizedCardPresenceContext() {
  const context = useContext(CardPresenceContext);
  if (context === undefined) {
    throw new Error('useOptimizedCardPresenceContext must be used within a CardPresenceProvider');
  }
  return context;
}

export function CardPresenceProvider({ children, boardId }: CardPresenceProviderProps) {
  const { user } = useAuth();
  const [presence, setPresence] = useState<CardPresenceState>({});
  const [isConnected, setIsConnected] = useState(false);
  const [connectionHealth, setConnectionHealth] = useState(true);
  const lastSentStatusMap = useRef<Map<string, "viewing" | "editing">>(new Map());

  
  // **OPTIMIZED: Removed manual interval refs - now managed by timeoutManager**
  const isActiveRef = useRef(true);
  const currentStatusRef = useRef<{[cardId: string]: "viewing" | "editing"}>({});
  const visibleCardsRef = useRef<Set<string>>(new Set());
  const presenceCacheRef = useRef<CardPresenceState>({});
  
  // **OPTIMIZED: Increased debounce from 100ms to 200ms to reduce requests**
  const debouncerRef = useRef(new PresenceDebouncer(200));
  const connectionMonitorRef = useRef(new ConnectionMonitor());
  const channelRef = useRef<any>(null);

  // **OPTIMIZED: Optimistic presence with longer timeout**
  const [optimisticPresence, setOptimisticPresence] = useState<CardPresenceState>({});
  
  // Viewport-based card tracking
  const trackCardVisibility = useCallback((cardId: string, isVisible: boolean) => {
    if (isVisible) {
      visibleCardsRef.current.add(cardId);
    } else {
      visibleCardsRef.current.delete(cardId);
      delete currentStatusRef.current[cardId];
    }
  }, []);

  // Helper function to ensure status is properly typed
  const normalizePresenceData = (data: any[]): CardPresenceUser[] => {
    return data.map(item => ({
      ...item,
      status: item.status === "editing" ? "editing" : "viewing" as "viewing" | "editing"
    }));
  };

  // **OPTIMIZED: Longer timeout for optimistic updates**
  const addOptimisticPresence = useCallback((cardId: string, status: "viewing" | "editing") => {
    if (!user?.id) return;
    
    const optimisticUpdate: CardPresenceUser = {
      id: `optimistic-${user.id}-${cardId}`,
      board_id: boardId,
      card_id: cardId,
      user_id: user.id,
      user_name: user.email ?? user.user_metadata?.full_name ?? "",
      status,
      updated_at: new Date().toISOString()
    };
    
    setOptimisticPresence(prev => {
      const newState = { ...prev };
      if (!newState[cardId]) {
        newState[cardId] = [];
      }
      
      const existingIndex = newState[cardId].findIndex(p => p.user_id === user.id);
      if (existingIndex >= 0) {
        newState[cardId][existingIndex] = optimisticUpdate;
      } else {
        newState[cardId].push(optimisticUpdate);
      }
      
      return newState;
    });
    
    // **NEW: Use timeout manager for optimistic timeout**
    const timeoutKey = `optimistic-${user.id}-${cardId}`;
    timeoutManager.setTimeout(timeoutKey, () => {
      setOptimisticPresence(prev => {
        const newState = { ...prev };
        if (newState[cardId]) {
          newState[cardId] = newState[cardId].filter(p => p.user_id !== user.id);
          if (newState[cardId].length === 0) {
            delete newState[cardId];
          }
        }
        return newState;
      });
    }, 2000);
  }, [user?.id, user?.email, user?.user_metadata?.full_name, boardId]);

  const clearOptimisticPresence = useCallback((cardId: string) => {
    if (!user?.id) return;
    
    const timeoutKey = `optimistic-${user.id}-${cardId}`;
    timeoutManager.clearTimeout(timeoutKey);
    
    setOptimisticPresence(prev => {
      const newState = { ...prev };
      if (newState[cardId]) {
        newState[cardId] = newState[cardId].filter(p => p.user_id !== user.id);
        if (newState[cardId].length === 0) {
          delete newState[cardId];
        }
      }
      return newState;
    });
  }, [user?.id]);

  // Merge optimistic and confirmed presence
  const mergedPresence = React.useMemo(() => {
    const merged = { ...presence };
    
    Object.entries(optimisticPresence).forEach(([cardId, optimisticUsers]) => {
      optimisticUsers.forEach(optimisticUser => {
        if (!merged[cardId]) {
          merged[cardId] = [];
        }
        
        const existingIndex = merged[cardId].findIndex(u => u.user_id === optimisticUser.user_id);
        if (existingIndex === -1) {
          merged[cardId].push(optimisticUser);
        }
      });
    });
    
    return merged;
  }, [presence, optimisticPresence]);

  // Smart state update with caching
  const updatePresenceState = useCallback((updates: CardPresenceUser[]) => {
    setPresence(prev => {
      const newState = { ...prev };
      
      updates.forEach(update => {
        const cardId = update.card_id;
        
        if (update.user_id === user?.id) {
          clearOptimisticPresence(cardId);
        }
        
        if (!newState[cardId]) {
          newState[cardId] = [];
        }
        
        const existingIndex = newState[cardId].findIndex(p => p.user_id === update.user_id);
        
        if (existingIndex >= 0) {
          newState[cardId][existingIndex] = update;
        } else {
          newState[cardId].push(update);
        }
      });
      
      presenceCacheRef.current = newState;
      
      return newState;
    });
  }, [user?.id, clearOptimisticPresence]);

  // Remove presence for specific user/card combinations
  const removePresenceState = useCallback((removals: { cardId: string; userId: string }[]) => {
    setPresence(prev => {
      const newState = { ...prev };
      
      removals.forEach(({ cardId, userId }) => {
        if (userId === user?.id) {
          clearOptimisticPresence(cardId);
        }
        
        if (newState[cardId]) {
          newState[cardId] = newState[cardId].filter(p => p.user_id !== userId);
          
          if (newState[cardId].length === 0) {
            delete newState[cardId];
          }
        }
      });
      
      presenceCacheRef.current = newState;
      return newState;
    });
  }, [user?.id, clearOptimisticPresence]);

  // **OPTIMIZED: Enhanced batch upsert with consolidated timeout management**
  const batchUpsertPresence = useCallback(async (updates: PresenceUpdate[]) => {
    if (!boardId || !user?.id || !isActiveRef.current || updates.length === 0) return;
    
    const visibleUpdates = updates.filter(update => 
      visibleCardsRef.current.has(update.cardId)
    );
    
    if (visibleUpdates.length === 0) return;

    // **OPTIMIZED: Use request manager to prevent excessive requests**
    const requestKey = `upsert-${boardId}`;
    const hasEditingStatus = visibleUpdates.some(u => u.status === "editing");
    
    if (!presenceRequestManager.canFetch(requestKey, hasEditingStatus)) {
      return;
    }
    
    try {
      const presenceData = visibleUpdates.map(update => ({
        card_id: update.cardId,
        board_id: boardId,
        user_id: user.id,
        user_name: user.email ?? user.user_metadata?.full_name ?? "",
        status: update.status,
        updated_at: new Date().toISOString(),
      }));

      const result = await presenceRequestManager.deduplicate(requestKey, async () => {
        const { data, error } = await supabase
          .from("card_presence")
          .upsert(presenceData, { 
            onConflict: "card_id,user_id",
            ignoreDuplicates: false 
          })
          .select();

        if (error) {
          throw error;
        }
        return data;
      });

      if (result) {
        const normalizedData = normalizePresenceData(result);
        updatePresenceState(normalizedData);
        connectionMonitorRef.current.onReconnectSuccess();
        presenceRequestManager.markFetch(requestKey);
      }
    } catch (error) {
      console.error('❌ Error batch upserting presence:', error);
      connectionMonitorRef.current.onConnectionError();
      
      // **NEW: Use timeout manager for retry logic**
      timeoutManager.setRetry(`presence-upsert-${boardId}`, () => {
        if (isActiveRef.current) {
          batchUpsertPresence(updates);
        }
      });
    }
  }, [boardId, user?.id, user?.email, user?.user_metadata?.full_name, updatePresenceState]);

  // **OPTIMIZED: Consolidated cleanup with timeout manager**
  const cleanupExpiredPresence = useCallback(async () => {
    if (!boardId) return;
    
    try {
      // **OPTIMIZED: Increased cleanup interval to 90 seconds**
      const ninetySecondsAgo = new Date(Date.now() - 90000).toISOString();
      
      const { data, error } = await supabase
        .from("card_presence")
        .delete()
        .eq("board_id", boardId)
        .lt("updated_at", ninetySecondsAgo)
        .select('card_id, user_id');

      if (error) {
        console.error('❌ Error cleaning up expired presence:', error);
        // **NEW: Use timeout manager for cleanup retry**
        timeoutManager.setRetry(`cleanup-${boardId}`, () => {
          if (isActiveRef.current) {
            cleanupExpiredPresence();
          }
        });
      } else if (data && data.length > 0) {
        const removals = data.map(item => ({
          cardId: item.card_id,
          userId: item.user_id
        }));
        removePresenceState(removals);
      }
    } catch (error) {
      console.error('❌ Exception in cleanupExpiredPresence:', error);
      timeoutManager.setRetry(`cleanup-${boardId}`, () => {
        if (isActiveRef.current) {
          cleanupExpiredPresence();
        }
      });
    }
  }, [boardId, removePresenceState]);

  // Enhanced clear card presence
  const clearCardPresence = useCallback(async (cardId: string) => {
    if (!cardId || !user?.id) return;
    
    delete currentStatusRef.current[cardId];
    visibleCardsRef.current.delete(cardId);
    
    clearOptimisticPresence(cardId);
    
    try {
      const { error } = await supabase
        .from("card_presence")
        .delete()
        .match({ card_id: cardId, user_id: user.id });

      if (error) {
        console.error('❌ Error clearing card presence:', error);
      } else {
        removePresenceState([{ cardId, userId: user.id }]);
      }
    } catch (error) {
      console.error('❌ Exception in clearCardPresence:', error);
    }
  }, [user?.id, removePresenceState, clearOptimisticPresence]);

  // **OPTIMIZED: Set card status with smarter batching**
  const setCardStatus = useCallback((cardId: string, status: "viewing" | "editing") => {
    if (!isActiveRef.current) return;
    
    currentStatusRef.current[cardId] = status;
    
    if (!visibleCardsRef.current.has(cardId)) {
      visibleCardsRef.current.add(cardId);
    }
    
    addOptimisticPresence(cardId, status);
    
    // **OPTIMIZED: Immediate updates only for editing, debounced for viewing**
    if (status === "editing") {
      batchUpsertPresence([{ cardId, status, timestamp: Date.now() }]);
    } else {
      debouncerRef.current.addUpdate(cardId, status, batchUpsertPresence);
    }
  }, [batchUpsertPresence, addOptimisticPresence]);

  // **OPTIMIZED: Smart board presence fetch with timeout manager**
  const fetchBoardPresence = useCallback(async () => {
    if (!boardId) return;

    const requestKey = `fetch-${boardId}`;
    const hasEditingUsers = Object.values(currentStatusRef.current).some(status => status === "editing");
    
    if (!presenceRequestManager.canFetch(requestKey, hasEditingUsers)) {
      // Use cached data if available and recent enough
      const cacheAge = presenceRequestManager.getCacheAge(requestKey);
      if (cacheAge < 30000 && Object.keys(presenceCacheRef.current).length > 0) {
        setPresence(presenceCacheRef.current);
        return;
      }
      return;
    }

    try {
      const result = await presenceRequestManager.deduplicate(requestKey, async () => {
        const { data, error } = await supabase
          .from("card_presence")
          .select("*")
          .eq("board_id", boardId)
          .order('updated_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        return data;
      });

      const presenceByCard: CardPresenceState = {};
      (result || []).forEach((row) => {
        const safeRow: CardPresenceUser = {
          ...row,
          status: row.status === "editing" ? "editing" : "viewing",
        };
        
        if (!presenceByCard[row.card_id]) {
          presenceByCard[row.card_id] = [];
        }
        presenceByCard[row.card_id].push(safeRow);
      });
      
      setPresence(presenceByCard);
      presenceCacheRef.current = presenceByCard;
      presenceRequestManager.markFetch(requestKey);
    } catch (error) {
      console.error('❌ Exception in fetchBoardPresence:', error);
      // **NEW: Use timeout manager for fetch retry**
      timeoutManager.setRetry(`fetch-${boardId}`, () => {
        if (isActiveRef.current) {
          fetchBoardPresence();
        }
      });
    }
  }, [boardId]);

  // Enhanced real-time handling with smart updates
  const handleRealtimeUpdate = useCallback((payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    switch (eventType) {
      case 'INSERT':
      case 'UPDATE':
        if (newRecord) {
          const safeRecord: CardPresenceUser = {
            ...newRecord,
            status: newRecord.status === "editing" ? "editing" : "viewing",
          };
          updatePresenceState([safeRecord]);
        }
        break;
        
      case 'DELETE':
        if (oldRecord) {
          removePresenceState([{
            cardId: oldRecord.card_id,
            userId: oldRecord.user_id
          }]);
        }
        break;
    }
  }, [updatePresenceState, removePresenceState]);

  // Setup connection health monitoring
  useEffect(() => {
    const monitor = connectionMonitorRef.current;
    
    const handleHealthChange = (isHealthy: boolean) => {
      setConnectionHealth(isHealthy);
    };
    
    monitor.addHealthCallback(handleHealthChange);
    
    return () => {
      monitor.removeHealthCallback(handleHealthChange);
    };
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        Object.keys(currentStatusRef.current).forEach(clearCardPresence);
        currentStatusRef.current = {};
        visibleCardsRef.current.clear();
        debouncerRef.current.clear();
        
        setOptimisticPresence({});
      } else {
        isActiveRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearCardPresence]);

  // **OPTIMIZED: Board-wide presence with consolidated timeout management**
  useEffect(() => {
    if (!boardId || !user?.id) return;

    isActiveRef.current = true;
    
    cleanupExpiredPresence();
    fetchBoardPresence();

    // **NEW: Use timeout manager for all intervals**
    const setupHeartbeat = () => {
      const sendHeartbeat = () => {
        if (isActiveRef.current && Object.keys(currentStatusRef.current).length > 0) {
          /*const updates = Object.entries(currentStatusRef.current)
            .filter(([cardId]) => visibleCardsRef.current.has(cardId))
            .map(([cardId, status]) => ({
              cardId,
              status,
              timestamp: Date.now()
            }));*/
            const updates: PresenceUpdate[] = [];

            for (const [cardId, status] of Object.entries(currentStatusRef.current)) {
                if (!visibleCardsRef.current.has(cardId)) continue;

                const last = lastSentStatusMap.current.get(cardId);
                if (last !== status) {
                    updates.push({ cardId, status, timestamp: Date.now() });
                    lastSentStatusMap.current.set(cardId, status);
                }
            }
          
          if (updates.length > 0) {
            const hasEditing = updates.some(u => u.status === "editing");
            batchUpsertPresence(updates);
            
            // **NEW: Dynamic heartbeat interval using timeout manager**
            const nextInterval = hasEditing ? 20000 : 60000;
            timeoutManager.setTimeout(`heartbeat-${boardId}`, sendHeartbeat, nextInterval);
          }
        }
      };

      // Initial heartbeat after 30s
      timeoutManager.setTimeout(`heartbeat-${boardId}`, sendHeartbeat, 30000);
    };

    // **NEW: Use timeout manager for cleanup interval - every 2 minutes**
    const scheduleCleanup = () => {
      timeoutManager.setTimeout(`cleanup-${boardId}`, () => {
        if (isActiveRef.current) {
          cleanupExpiredPresence();
          scheduleCleanup(); // Reschedule
        }
      }, 120000);
    };

    scheduleCleanup();

    // Enhanced real-time subscription with timeout manager
    const setupChannel = () => {
      const channel = supabase
        .channel(`optimized_board_card_presence:${boardId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "card_presence",
            filter: `board_id=eq.${boardId}`,
          },
          handleRealtimeUpdate
        )
        .subscribe((status) => {
          setIsConnected(status === 'SUBSCRIBED');
          
          if (status === 'SUBSCRIBED') {
            connectionMonitorRef.current.onReconnectSuccess();
            setupHeartbeat();
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Optimized presence subscription error');
            connectionMonitorRef.current.onConnectionError();
            
            if (connectionMonitorRef.current.shouldReconnect()) {
              const delay = Math.min(connectionMonitorRef.current.getReconnectDelay(), 5000);
              connectionMonitorRef.current.onReconnectAttempt();
              
              // **NEW: Use timeout manager for reconnection**
              timeoutManager.setTimeout(`reconnect-${boardId}`, () => {
                setupChannel();
              }, delay);
            }
          }
        });
        
      channelRef.current = channel;
      return channel;
    };

    const channel = setupChannel();

    return () => {
      // **NEW: Use timeout manager for cleanup**
      timeoutManager.clearTimeout(`heartbeat-${boardId}`);
      timeoutManager.clearTimeout(`cleanup-${boardId}`);
      timeoutManager.clearTimeout(`reconnect-${boardId}`);
      
      debouncerRef.current.clear();
      
      isActiveRef.current = false;
      Object.keys(currentStatusRef.current).forEach(clearCardPresence);
      currentStatusRef.current = {};
      visibleCardsRef.current.clear();
      
      setOptimisticPresence({});
      
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      setIsConnected(false);
      
      connectionMonitorRef.current.reset();
    };
  }, [boardId, user?.id, batchUpsertPresence, cleanupExpiredPresence, fetchBoardPresence, clearCardPresence, handleRealtimeUpdate]);

  const contextValue: CardPresenceContextType = {
    presence: mergedPresence,
    isConnected,
    connectionHealth,
    setCardStatus,
    clearCardPresence,
    trackCardVisibility,
  };

  return (
    <CardPresenceContext.Provider value={contextValue}>
      {children}
    </CardPresenceContext.Provider>
  );
}


